"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";

import {
  LOVE_CHART_CHECK_FIELD_IDS,
  LOVE_CHART_CHECK_FORM,
  LOVE_CHART_CHECK_FRAME,
  LOVE_CHART_FORM_RESERVE,
} from "./love-chart-check-form";

// The /love-horoscope/ check's boundary (the rashi check's pattern,
// RashiCheckLazy): the server page imports only this module, and it owns the
// one dynamic() to the island. The form, place search, engine client and answer
// load as an async chunk; the first load gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer, so a
// reader scrolling the guide never downloads the island; a press, focus or
// submit on the shell does.
//
// The slot reserves the form's tallest state, so neither shell -> form nor an
// engine error after a slow reply moves the guide below; the answer's taller
// room is added by the island in the press that sends the request
// (LOVE_CHART_ANSWER_RESERVE). Natural heights measured 2026-10-14 (next dev +
// Playwright, 8 widths, the engine mocked with two answers and a failure): the
// form is 414 at 320-414 px wide, 398 at 480-639 and 356 from 640; with the
// engine-failure alert it is 500 at 320-360, 482 at 390-414, 466 at 480-639 and
// 424 from 640 (measured with the check in the page's hero). Hence 520 px on
// phones and 460 px from the sm breakpoint (LOVE_CHART_FORM_RESERVE).
// translate="no" covers the shell, form and answer, which re-render on "check
// another" (tests/translate-safe-islands.test.ts).
const SHELL_PROPS = { ...LOVE_CHART_CHECK_FORM, className: LOVE_CHART_CHECK_FRAME } as const;

const LoveChartCheck = dynamic(() => import("./LoveChartCheck"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: LOVE_CHART_CHECK_FIELD_IDS.date,
  tob: LOVE_CHART_CHECK_FIELD_IDS.time,
  place: LOVE_CHART_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || LOVE_CHART_CHECK_FIELD_IDS.date;
}

export default function LoveChartCheckLazy() {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // Focus follows the visitor into the real form after a keyboard focus, a
  // mouse press or a submit, and never after a touch, which may start a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(LOVE_CHART_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-love-chart-check-slot=""
      className={`flex flex-col ${LOVE_CHART_FORM_RESERVE}`}
      {...shellIntent}
    >
      {active ? (
        <LoveChartCheck focusFieldId={focusFieldId} />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
